import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { getUserTags } from '../../services/api';

const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);

const UploadReviewModal = ({
  files = [],
  onConfirm,
  onCancel,
  allowedExtensions = ['pdf','doc','docx','png','jpg','jpeg','csv','xls','xlsx'],
  maxSizeMB = 50,
  existingFilenames = [],
}) => {
  const [selected, setSelected] = useState(() => new Set(files.map((f) => f.name)));
  const [allowDuplicates, setAllowDuplicates] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [tags, setTags] = useState([]);
  const [templatePath, setTemplatePath] = useState('');
  const [renameOverrides, setRenameOverrides] = useState({}); // name -> override
  const [allTags, setAllTags] = useState([]);
  const [keyToValues, setKeyToValues] = useState({});
  const [valueSuggestions, setValueSuggestions] = useState([]);
  const [showValueSuggestions, setShowValueSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  // Helpers to align rename logic with Photos modal
  const sanitize = (s) => (s || '')
    .toString()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_\-]+/g, '')
    .replace(/^_+|_+$/g, '') || '';

  const tagKVs = useMemo(() => tags.map(raw => {
    const m = String(raw).match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.+)$/);
    if (m) return { key: m[1].trim().toLowerCase(), value: m[2].trim(), raw };
    return { key: null, value: String(raw).trim(), raw };
  }), [tags]);
  const tagValues = useMemo(() => tagKVs.map(kv => kv.value).filter(Boolean), [tagKVs]);
  const getValueForKey = useCallback((rx) => {
    const kv = tagKVs.find(k => k.key && rx.test(k.key));
    return kv ? kv.value : null;
  }, [tagKVs]);

  // Load user's tags once and build key -> set(values) map from names like "Key: Value"
  useEffect(() => {
    const loadTags = async () => {
      try {
        const data = await getUserTags();
        setAllTags(Array.isArray(data) ? data : []);
      } catch (_) {
        setAllTags([]);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    const next = {};
    const ensureSet = (k) => {
      const key = String(k || '').trim().toLowerCase();
      if (!key) return null;
      if (!next[key]) next[key] = new Set();
      return next[key];
    };
    for (const t of allTags) {
      const raw = (t && t.name) ? String(t.name) : '';
      const m = raw.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.+)$/);
      if (m) {
        const k = m[1];
        const v = m[2];
        const set = ensureSet(k);
        if (set && v) set.add(String(v).trim());
      }
    }
    // Convert to arrays for rendering
    const obj = {};
    Object.keys(next).forEach(k => obj[k] = Array.from(next[k]));
    setKeyToValues(obj);
  }, [allTags]);

  const updateValueSuggestions = useCallback((input) => {
    const str = String(input || '');
    const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
    if (!m) {
      setValueSuggestions([]);
      setShowValueSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    const key = m[1].trim().toLowerCase();
    const prefix = (m[2] || '').toString().trim().toLowerCase();
    const candidates = keyToValues[key] || [];
    let filtered = candidates;
    if (prefix) {
      filtered = candidates.filter(v => String(v).toLowerCase().includes(prefix));
    }
    // Limit to 8 suggestions
    filtered = filtered.slice(0, 8);
    setValueSuggestions(filtered);
    setShowValueSuggestions(filtered.length > 0);
    setActiveSuggestionIndex(filtered.length ? 0 : -1);
  }, [keyToValues]);

  const findAge = useCallback(() => {
    const candidates = [...tagValues];
    for (let raw of candidates) {
      const n = (raw || '').toString().trim().toLowerCase().replace(/[,]/g, '');
      if (!n) continue;
      let m = n.match(/^(\d{1,2})\s*(y|yr|yrs|year|years)$/i); if (m) return `${m[1]}y`;
      m = n.match(/^(\d{1,3})\s*(m|mo|mos|month|months)$/i); if (m) return `${m[1]}mo`;
      m = n.match(/^age[:=\s]+(\d{1,3})\s*(years?|yrs?)?$/i); if (m) return `${m[1]}y`;
      m = n.match(/^age[:=\s]+(\d{1,3})\s*(months?|mos?)$/i); if (m) return `${m[1]}mo`;
      m = n.match(/^(\d{1,2})y$/i); if (m) return `${m[1]}y`;
      m = n.match(/^(\d{1,3})mo$/i); if (m) return `${m[1]}mo`;
      m = n.match(/^(\d{1,2})\s*[- ]?month(s)?$/i); if (m) return `${m[1]}mo`;
    }
    return null;
  }, [tagValues]);

  const looksLikeName = (n) => {
    if (!n) return false;
    if (/\s+/.test(n) && /^[A-Za-z][A-Za-z\-']+(\s+[A-Za-z\-']+)+$/.test(n)) return true;
    if (/^[A-Z][a-z]{2,}$/.test(n)) return true;
    return false;
  };

  const findPerson = useCallback(() => {
    const val = getValueForKey(/^(person|name|who)$/i);
    if (val) return val;
    const candidate = tagValues.find(looksLikeName);
    return candidate || null;
  }, [getValueForKey, tagValues]);

  const findEvent = useCallback(() => {
    const keyed = getValueForKey(/^(event|occasion)$/i);
    if (keyed) return keyed;
    const keywords = /(birthday|wedding|anniversary|graduation|vacation|party|hike|trip|holiday|christmas|new\s*year|halloween|easter|festival|concert)/i;
    for (const n of tagValues) { if (keywords.test(n)) return n; }
    return null;
  }, [getValueForKey, tagValues]);

  const buildProposedName = useCallback((file) => {
    const name = file?.name || '';
    const ext = (name.split('.').pop() || '').toLowerCase();
    const imageExts = ['jpg','jpeg','png','gif','webp','bmp','svg'];
    const videoExts = ['mp4','mov','avi','mkv','wmv','flv','webm','m4v'];
    const audioExts = ['mp3','wav','flac','m4a','aac','ogg','wma'];

    const person = findPerson();
    const age = findAge();
    const eventVal = findEvent();
    const second = age || eventVal || null;

    if (imageExts.includes(ext) || videoExts.includes(ext)) {
      const p = sanitize(person) || 'person';
      const s = sanitize(second) || (age ? '1y' : 'event');
      return `${p}_${s}`;
    }
    const isSpreadsheet = ['csv','xls','xlsx'].includes(ext);
    if (isSpreadsheet) {
      // Use template-ish fields if tags contain them
      const proj = sanitize(getValueForKey(/^(project)$/i)) || 'project';
      const kind = 'data';
      const ver = 'v1';
      return `${proj}_${kind}_${ver}`;
    }
    return name.replace(/\.[^.]+$/, '') || 'file';
  }, [findPerson, findAge, findEvent, getValueForKey]);

  const extAllowed = (name) => {
    const ext = String(name).toLowerCase().split('.').pop();
    return allowedExtensions.includes(ext);
  };

  const validations = useMemo(() => {
    const existingSet = new Set((existingFilenames || []).map((n) => String(n).toLowerCase()));
    return files.map((file) => {
      const tooLarge = file.size > maxSizeMB * 1024 * 1024;
      const badExt = !extAllowed(file.name);
      const isDuplicate = existingSet.has(String(file.name).toLowerCase());
      const valid = !tooLarge && !badExt;
      return { file, tooLarge, badExt, isDuplicate, valid };
    });
  }, [files, allowedExtensions, maxSizeMB, existingFilenames]);

  const allInvalid = useMemo(() => validations.every((v) => !v.valid), [validations]);
  const selectedValidations = useMemo(
    () => validations.filter((v) => selected.has(v.file.name)),
    [validations, selected]
  );
  const hasSelected = selected.size > 0;
  const hasInvalidSelected = selectedValidations.some((v) => !v.valid);
  const hasDuplicateSelected = selectedValidations.some((v) => v.isDuplicate);

  const toggleSelected = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(files.map((f) => f.name)));
  const selectNone = () => setSelected(new Set());
  const removeInvalids = () => setSelected(new Set(validations.filter((v) => v.valid).map((v) => v.file.name)));

  const confirm = () => {
    const chosen = validations
      .filter((v) => selected.has(v.file.name))
      .filter((v) => v.valid)
      .map((v) => v.file);
    if (chosen.length === 0) return;
    if (hasDuplicateSelected && !allowDuplicates) return;
    const tagList = tags.map(t => t.trim()).filter(Boolean);
    const overrides = chosen.map(f => renameOverrides[f.name] || '');
    onConfirm(chosen, { tagIds: tagList, templatePath: templatePath.trim(), renameOverrides: overrides });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Review files before upload</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-auto">
          {/* Tags & Template */}
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Add tag (Key: Value supported)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      value={newTagName}
                      onChange={(e) => { setNewTagName(e.target.value); updateValueSuggestions(e.target.value); }}
                      onFocus={() => updateValueSuggestions(newTagName)}
                      onKeyDown={(e) => {
                        if (!showValueSuggestions || valueSuggestions.length === 0) return;
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setActiveSuggestionIndex((idx) => (idx + 1) % valueSuggestions.length);
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setActiveSuggestionIndex((idx) => (idx - 1 + valueSuggestions.length) % valueSuggestions.length);
                        } else if (e.key === 'Enter') {
                          const str = String(newTagName || '');
                          const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
                          if (m && activeSuggestionIndex >= 0) {
                            e.preventDefault();
                            const key = m[1].trim();
                            const chosen = valueSuggestions[activeSuggestionIndex];
                            setNewTagName(`${key}: ${chosen}`);
                            setShowValueSuggestions(false);
                          }
                        } else if (e.key === 'Escape') {
                          setShowValueSuggestions(false);
                        }
                      }}
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded"
                      placeholder="Name: Lewis"
                    />
                    {showValueSuggestions && valueSuggestions.length > 0 && (
                      <div className="absolute left-0 right-0 mt-1 max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-10">
                        {valueSuggestions.map((v, idx) => (
                          <div
                            key={v + idx}
                            className={`px-3 py-2 text-sm cursor-pointer ${idx === activeSuggestionIndex ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                            onMouseEnter={() => setActiveSuggestionIndex(idx)}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const str = String(newTagName || '');
                              const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
                              const key = m ? m[1].trim() : '';
                              setNewTagName(`${key}: ${v}`);
                              setShowValueSuggestions(false);
                            }}
                          >
                            {v}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => { if (newTagName.trim()) { setTags(prev => [...prev, newTagName.trim()]); setNewTagName(''); setShowValueSuggestions(false); } }} className="px-3 py-1 bg-blue-600 text-white rounded">Add</button>
                </div>
                {tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tags.map((t, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                        {t}
                        <button className="ml-1" onClick={() => setTags(prev => prev.filter((_, idx) => idx !== i))}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Template path (optional)</label>
                <input value={templatePath} onChange={(e) => setTemplatePath(e.target.value)} className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded" placeholder="Photos/<Person>/<Age>/" />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Allowed types: {allowedExtensions.join(', ').toUpperCase()} • Max size: {maxSizeMB} MB per file
            </div>
            <div className="space-x-2">
              <button onClick={selectAll} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded">Select all</button>
              <button onClick={selectNone} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded">Clear</button>
              <button onClick={removeInvalids} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded">Remove invalid</button>
            </div>
          </div>

          {files.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-10">No files selected</div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
              {validations.map(({ file, tooLarge, badExt, isDuplicate, valid }) => (
                <label key={file.name + String(file.size)} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={selected.has(file.name)}
                    onChange={() => toggleSelected(file.name)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white truncate">{file.name}</span>
                      {!valid && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Invalid</span>
                      )}
                      {isDuplicate && (
                        <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">Duplicate</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {bytesToMB(file.size)} MB
                      {tooLarge && <span className="ml-2 text-red-600">Exceeds {maxSizeMB}MB</span>}
                      {badExt && <span className="ml-2 text-red-600">Disallowed type</span>}
                    </div>
                    {/* Per-file rename override */}
                    <div className="mt-2">
                      <input
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded"
                        placeholder={buildProposedName(file)}
                        value={renameOverrides[file.name] || ''}
                        onChange={(e) => setRenameOverrides(prev => ({ ...prev, [file.name]: e.target.value }))}
                      />
                      <p className="mt-1 text-[11px] text-gray-500">Leave blank to use the suggested name above.</p>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {hasDuplicateSelected && (
            <label className="flex items-center gap-2 text-sm text-yellow-800 bg-yellow-50 dark:bg-yellow-900/40 dark:text-yellow-200 p-3 rounded">
              <input
                type="checkbox"
                className="rounded border-yellow-400 text-yellow-600 focus:ring-yellow-500"
                checked={allowDuplicates}
                onChange={(e) => setAllowDuplicates(e.target.checked)}
              />
              Some selected files share a name with existing files. Proceed anyway?
            </label>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <button onClick={onCancel} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 flex-1">Cancel</button>
          <button
            onClick={confirm}
            disabled={!hasSelected || allInvalid || hasInvalidSelected || (hasDuplicateSelected && !allowDuplicates)}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white flex-1"
          >
            Upload {selected.size} file(s)
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadReviewModal;





